// The moon. It used to be a 290 m crate with two darker slabs stuck on its
// face for maria, hung over the map's centre and turned to stare at the
// island — full every night, flat as a sign. Now it is the moon a night
// pilot navigates by, drawn like the sun (sun.js) on one quad that faces the
// eye from the far plane:
//
//   * Its face is the real near side. The dark maria sit where they sit —
//     the Sea of Rains up and to the left, Tranquillity right of centre, the
//     round Sea of Crises alone by the right-hand limb, the great Ocean of
//     Storms spilling down the left — and the bright ray craters splash
//     across the highlands: Tycho low in the south, Copernicus and Kepler in
//     the Ocean, Aristarchus the brightest spot on the whole face.
//   * It has phases. Over a lunar month of game days it swells to full and
//     wanes to a fat gibbous and back, the same for every pilot in the sky,
//     its bright limb swapping sides from waxing to waning; the dark side
//     still blots out the stars behind it, faintly lit by the earth. (It
//     rides exactly opposite the sun, as a full moon does, so the phase is
//     lit from a sun carried on round the sky's turning — the night's key
//     light and the sea's glitter path stay where the moon is.)
//   * A full moon is bright right out to its rim — the dust on the moon
//     throws light straight back where it came from — not shaded like a
//     ball; only the terminator falls off, soft.
//   * Rising and setting it is honey and orange through the same air that
//     reddens the sun, sitting fat and low on the sea.
//   * A thin halo of moonlight hangs round it, wider through haze; an
//     overcast veils it to a pale smudge.
//
// It keeps the old moon's size in the sky (about two degrees across, four
// times the real one — anything smaller and its face is a white dot) and
// its road: opposite the sun, so the night's key light, the glitter path on
// the sea and the shadows all still come from where you see it.

import * as THREE from "three";
import { airmass } from "./sun.js";
import { CELESTIAL_POLE } from "./stars.js";

// Angular radius of the disc (tangent of the half-angle): 1°, the crate's
// old size, so the night sky's landmark didn't shrink.
export const MOON_RADIUS = Math.tan((1.0 * Math.PI) / 180);
// The quad's half-width, room for the halo to fade out before its edge.
const EXTENT = MOON_RADIUS * 9;
// A lunar month, in game days (world.js DAY_SECONDS — twenty minutes): one
// round sees one moon, an evening's play sees it wax and wane.
export const LUNATION_DAYS = 29.5;
// How far off full it goes: 70° leaves a fat gibbous (two-thirds lit) at the
// thinnest, so every night still has its moon to fly by.
export const PHASE_MAX = (70 * Math.PI) / 180;
// Optical depths for moonlight through the air (as the sun's, a touch
// gentler — the eye sees a rising moon honey-coloured, not ember-red).
const TAU = [0.02, 0.05, 0.11];
const EARTHSHINE = 0.06;
const TEX = 256;

/**
 * The moon's phase angle at sky clock `t` (s) with days of `daySec`: 0 full,
 * positive waning (it lags the sun's opposite point, so its lit limb leads),
 * negative waxing. Smooth — nobody sees it jump at midnight.
 */
export function moonPhase(t, daySec) {
  return PHASE_MAX * Math.sin((2 * Math.PI * t) / (daySec * LUNATION_DAYS));
}

/** The lit fraction of the disc at phase angle `phi`. */
export function litFraction(phi) {
  return (1 + Math.cos(phi)) / 2;
}

// The near side's seas, in selenographic degrees (longitude east positive,
// latitude north positive) with their half-widths: [lon, lat, rLon, rLat,
// darkness]. Rough outlines — irregular edges come from noise below.
const MARIA = [
  [-57, 18, 16, 22, 0.8], // Oceanus Procellarum, the Ocean of Storms
  [-47, 32, 13, 12, 0.75],
  [-42, 4, 12, 13, 0.75],
  [-62, -2, 9, 12, 0.7],
  [-28, 26, 10, 9, 0.8],
  [-16, 33, 17, 13, 1], // Mare Imbrium, the Sea of Rains
  [17.5, 27, 11, 10, 0.9], // Serenitatis
  [24, 17, 8, 8, 0.9],
  [31, 8.5, 11, 9, 1], // Tranquillitatis
  [22, 3, 7, 5, 0.9],
  [59, 17, 7.5, 6, 1], // Crisium, alone by the limb
  [51, -6, 7, 10, 0.85], // Fecunditatis
  [35, -15, 5, 5, 0.85], // Nectaris
  [-15, -19, 12, 9, 0.8], // Nubium
  [-39, -24, 5.5, 5.5, 0.9], // Humorum
  [-26, 6, 11, 7, 0.7], // Insularum
  [-23, -9, 8, 6, 0.7], // Cognitum
  [2, 2, 5, 4, 0.6], // Sinus Medii
  [3.6, 13, 5, 4, 0.8], // Vaporum
  [0, 55, 36, 6, 0.45], // Frigoris, the long cold band across the north
  [-68, -5, 3, 3, 0.9], // Grimaldi's dark floor
  [-9.3, 51.6, 2, 1.6, 0.8], // Plato
];
// Bright young craters: [lon, lat, radius°, brightness, rays].
const CRATERS = [
  [-11.4, -43.3, 2, 0.3, 1], // Tycho: rays across half the face
  [-20, 9.6, 2.2, 0.25, 0.6], // Copernicus
  [-38, 8.1, 1.4, 0.25, 0.4], // Kepler
  [-47.5, 23.7, 1.3, 0.45, 0], // Aristarchus, the brightest spot there is
  [-14, -58, 6, 0.08, 0], // Clavius's big old walls
  [61, -9, 1.2, 0.25, 0.3], // Langrenus
  [26, -25, 1.2, 0.3, 0], // Theophilus
];

// The same, with what every pixel would otherwise recompute: a sea's
// cos(latitude); a crater's unit vector and how far its light reaches.
const RAD = Math.PI / 180;
const MARIA_P = MARIA.map((m) => [...m, Math.cos(m[1] * RAD)]);
const CRATERS_P = CRATERS.map(([lo, la, rad, br, rays]) => {
  const reach = Math.max(rad * 1.6, 57 * rays);
  return [lo, la, rad, br, rays, Math.cos(la * RAD) * Math.sin(lo * RAD), Math.sin(la * RAD),
    Math.cos(la * RAD) * Math.cos(lo * RAD), Math.cos(reach * RAD), reach];
});

const smooth = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// Value noise on the sphere's face, deterministic.
function hashN(i, j, seed) {
  let n = (Math.imul(i, 0x27d4eb2d) + Math.imul(j, 0x165667b1) + Math.imul(seed, 0x9e3779b9)) >>> 0;
  n = Math.imul(n ^ (n >>> 15), 0x85ebca6b) >>> 0;
  return ((n ^ (n >>> 13)) >>> 0) / 4294967296;
}
function vnoise(x, y, seed) {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = x - ix, fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const h00 = hashN(ix, iy, seed), h10 = hashN(ix + 1, iy, seed);
  const h01 = hashN(ix, iy + 1, seed), h11 = hashN(ix + 1, iy + 1, seed);
  const a = h00 + (h10 - h00) * sx;
  const b = h01 + (h11 - h01) * sx;
  return a + (b - a) * sy;
}

/**
 * The face's brightness (0..1, a screen brightness under full sun) at a
 * point on the disc as seen from here: x right (the moon's east limb, where
 * Crisium is), y up (north), both -1..1.
 */
export function moonAlbedo(x, y) {
  const rr = x * x + y * y;
  if (rr >= 1) return 0;
  const z = Math.sqrt(1 - rr);
  const lat = (Math.asin(y) * 180) / Math.PI;
  const lon = (Math.atan2(x, z) * 180) / Math.PI;
  // Highland mottle.
  const n1 = vnoise(x * 9, y * 9, 1), n2 = vnoise(x * 23, y * 23, 2), n3 = vnoise(x * 61, y * 61, 3);
  let a = 0.8 + 0.08 * (n1 - 0.5) + 0.06 * (n2 - 0.5) + 0.05 * (n3 - 0.5);
  // The seas: dark, smooth, their shores ragged.
  let sea = 0;
  for (let m = 0; m < MARIA_P.length; m++) {
    const [lo, la, rl, rb, k, cosLa] = MARIA_P[m];
    const dl = ((lon - lo) * cosLa) / rl;
    const db = (lat - la) / rb;
    const d0 = dl * dl + db * db;
    if (d0 > 2.6) continue; // past the ragged shore's reach
    const d = Math.sqrt(d0)
      + 0.4 * (vnoise(x * 7 + lo, y * 7 + la, 4) - 0.5) + 0.2 * (vnoise(x * 19, y * 19, 11) - 0.5);
    sea = Math.max(sea, k * (1 - smooth(0.55, 1.2, d)));
  }
  a = a * (1 - 0.47 * sea) + 0.03 * (n2 - 0.5) * sea;
  // The highlands are pocked with small fresh craters, bright specks
  // thickest in the south; the seas were flooded smooth over theirs.
  const pock = vnoise(x * 72, y * 72, 13) * 0.6 + vnoise(x * 150, y * 150, 14) * 0.4;
  a += (0.07 + 0.05 * Math.max(0, -y)) * smooth(0.62, 0.8, pock) * (1 - 0.8 * sea);
  // The young craters and their rays. (x, y, z) is the point's own unit
  // vector, so its distance from a crater is one dot product.
  for (let c = 0; c < CRATERS_P.length; c++) {
    const [lo, la, rad, br, rays, cx, cy, cz, cosReach, reach] = CRATERS_P[c];
    const cosd = x * cx + y * cy + z * cz;
    if (cosd < cosReach) continue;
    const dist = (Math.acos(Math.min(1, cosd)) * 180) / Math.PI;
    a += br * (1 - smooth(rad * 0.6, rad * 1.6, dist));
    if (rays > 0 && dist > rad && dist < reach) {
      const la0 = (la * Math.PI) / 180, dlo = ((lon - lo) * Math.PI) / 180;
      const la1 = (lat * Math.PI) / 180;
      const bear = Math.atan2(
        Math.sin(dlo) * Math.cos(la1),
        Math.cos(la0) * Math.sin(la1) - Math.sin(la0) * Math.cos(la1) * Math.cos(dlo),
      );
      const bins = 22;
      const f = ((bear / (2 * Math.PI)) + 1) * bins;
      const bin = Math.floor(f), frac = f - bin;
      const len = (12 + 45 * vnoise(bin + 0.5, lo, 9)) * rays;
      const strength = vnoise(bin + 0.5, la, 10) > 0.35 ? 1 : 0;
      const across = 1 - smooth(0.03, 0.3, Math.abs(frac - 0.5 + 0.3 * (vnoise(bin + 0.5, la, 12) - 0.5)));
      a += 0.1 * strength * across * (1 - smooth(len * 0.4, len, dist));
    }
  }
  return Math.min(1, Math.max(0.2, a));
}

// The face, baked into a texture: brightness only (the colour is the
// light's), with mipmaps so a distant-looking 30-pixel moon doesn't shimmer.
// Baking it is a few tens of milliseconds of arithmetic — too long to spend
// in one piece while the page is booting — so it starts a plain grey disc
// and fills in a few rows at a time when the page has a moment to spare.
// The moon is down at the mid-morning the sky boots to, so nobody sees the
// grey one.
const BAKE_ROWS = 16;
function moonTexture() {
  const data = new Uint8Array(TEX * TEX * 4).fill(204);
  const tex = new THREE.DataTexture(data, TEX, TEX, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  const idle = globalThis.requestIdleCallback ?? ((fn) => setTimeout(fn, 0));
  let row = 0;
  const bake = () => {
    const end = Math.min(TEX, row + BAKE_ROWS);
    for (let j = row; j < end; j++) {
      for (let i = 0; i < TEX; i++) {
        const x = ((i + 0.5) / TEX) * 2 - 1, y = ((j + 0.5) / TEX) * 2 - 1;
        // Just past the limb, keep the rim's value so filtering doesn't
        // pull black in at the edge.
        const l = Math.hypot(x, y);
        const k = l > 0.985 ? 0.985 / l : 1;
        const v = Math.round(moonAlbedo(x * k, y * k) * 255);
        const o = (j * TEX + i) * 4;
        data[o] = data[o + 1] = data[o + 2] = v;
      }
    }
    row = end;
    if (row < TEX) idle(bake);
    else tex.needsUpdate = true;
  };
  idle(bake);
  return tex;
}

const VERT = /* glsl */ `
uniform vec3 uDir;
uniform float uExtent;
varying vec2 vQ;
void main() {
  // The same quad as the sun's: round the moon's direction, one unit out,
  // the world's up its up, pinned to the far plane.
  vec3 d = normalize((viewMatrix * vec4(uDir, 0.0)).xyz);
  vec3 up = (viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz;
  vec3 r = cross(d, up);
  r = dot(r, r) > 1e-6 ? normalize(r) : vec3(1.0, 0.0, 0.0);
  vec3 u = cross(r, d);
  vQ = position.xy * uExtent;
  gl_Position = projectionMatrix * vec4(d + r * vQ.x + u * vQ.y, 1.0);
  gl_Position.z = gl_Position.w;
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uFace;
uniform vec3 uL;        // toward the sun, in the disc's frame (x right, y up, z at us)
uniform vec3 uTint;     // moonlight's colour through the air, times its brightness
uniform float uR;
uniform float uExtent;
uniform float uFlat;    // squashed on the horizon, as the sun is
uniform float uHalo;
uniform float uVeil;
uniform float uDark;    // how dark the sky is: 0 twilight .. 1 night
uniform vec3 uSky;      // the sky's own colour (linear), which lies in front of it
uniform float uVis;
varying vec2 vQ;
void main() {
  vec2 p = vec2(vQ.x, vQ.y / (1.0 - uFlat)) / uR;
  float rd = length(p);
  float soft = max(fwidth(rd), 1e-4) + 0.9 * uVeil;
  float cover = 1.0 - smoothstep(1.0 - soft, 1.0 + soft, rd);
  vec2 f = p / max(rd, 1.0);
  vec3 n = vec3(f, sqrt(max(0.0, 1.0 - dot(f, f))));
  // Lommel-Seeliger: the moon's dust throws light back the way it came, so
  // a full moon is as bright at its rim as its heart. The terminator falls
  // off softly over the last of the lit side.
  float mu0 = dot(n, uL);
  float lit = mu0 > 0.0 ? 2.0 * mu0 / (mu0 + n.z + 1e-3) : 0.0;
  lit *= smoothstep(-0.02, 0.1, mu0);
  float albedo = texture2D(uFace, f * 0.5 + 0.5).r;
  vec3 face = uTint * albedo * min(lit, 1.15);
  // Earthshine: the dark side, lit faint and blue by a full earth.
  face += vec3(0.55, 0.62, 0.78) * ${EARTHSHINE.toFixed(3)} * albedo * (1.0 - smoothstep(0.0, 0.15, mu0)) * uDark;
  // The halo: moonlight scattered off the air round it, faint and wide.
  float rg = length(vQ) / uR;
  float halo = uHalo / (1.0 + rg * rg) * (1.0 - smoothstep(0.5, 1.0, length(vQ) / uExtent));
  cover *= 1.0 - 0.85 * uVeil;
  // The air is in front of the moon, so its dark side is the sky's colour,
  // not a hole: at night the disc blots out the stars behind it and puts the
  // sky's navy back in their place; in twilight it adds its light to the
  // sky and the shadow side is simply the sky.
  float a = cover * uDark;
  vec3 col = pow(max(face * cover + uTint * halo * (1.0 - cover), vec3(1e-6)), vec3(2.2)) + uSky * a;
  gl_FragColor = vec4(col, a) * uVis;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/**
 * Build the moon into `scene`. Returns { mesh, hdr, update(state) }: update
 * takes { dir (unit, toward the moon), t (sky clock, s), daySec, nightK,
 * gray (the round's overcast), sky (the sky's colour behind it) }. After update, `.lit` is the lit
 * fraction times how far up it is — how much moonlight is washing the stars.
 */
export function buildMoon(scene) {
  const uniforms = {
    uDir: { value: new THREE.Vector3(0, 1, 0) },
    uExtent: { value: EXTENT },
    uFace: { value: moonTexture() },
    uL: { value: new THREE.Vector3(0, 0, 1) },
    uTint: { value: new THREE.Color() },
    uR: { value: MOON_RADIUS },
    uFlat: { value: 0 },
    uHalo: { value: 0 },
    uVeil: { value: 0 },
    uDark: { value: 1 },
    uSky: { value: new THREE.Color() },
    uVis: { value: 1 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    fog: false,
    blending: THREE.CustomBlending,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  mesh.frustumCulled = false;
  mesh.name = "moon";
  scene.add(mesh);

  const toSun = new THREE.Vector3(), ahead = new THREE.Vector3();
  const r = new THREE.Vector3(), u = new THREE.Vector3();
  const Y = new THREE.Vector3(0, 1, 0);
  return {
    mesh,
    hdr: 1,
    lit: 0,
    phase: 0,
    update({ dir, t, daySec, nightK, gray, sky }) {
      uniforms.uDir.value.copy(dir);
      const e = dir.y;
      // Where the sun is, seen from the moon: its opposite point, carried on
      // along the sky's turning by the phase angle.
      const phi = moonPhase(t, daySec);
      this.phase = phi;
      ahead.crossVectors(CELESTIAL_POLE, dir).negate().normalize();
      toSun.copy(dir).multiplyScalar(-Math.cos(phi)).addScaledVector(ahead, Math.sin(phi));
      // Into the disc's frame, built as the vertex shader builds it.
      r.crossVectors(dir, Y);
      if (r.lengthSq() < 1e-6) r.set(1, 0, 0);
      r.normalize();
      u.crossVectors(r, dir);
      uniforms.uL.value.set(toSun.dot(r), toSun.dot(u), -toSun.dot(dir)).normalize();
      // Honey and orange low down, through the same air as the sun.
      const m = airmass(e);
      // (Relative to straight up, so the zenith moon is the silver below.)
      const tr = Math.exp(-TAU[0] * (m - 1)), tg = Math.exp(-TAU[1] * (m - 1)), tb = Math.exp(-TAU[2] * (m - 1));
      const top = Math.max(tr, tg, tb);
      const bright = (0.55 + 0.45 * top) * this.hdr;
      // A cool silver white overhead.
      uniforms.uTint.value.setRGB((0.96 * tr) / top, (0.97 * tg) / top, tb / top).multiplyScalar(bright);
      const low = 1 - Math.min(1, Math.max(0, e / 0.08));
      uniforms.uFlat.value = 0.15 * low * low;
      const frac = litFraction(phi);
      uniforms.uHalo.value = (0.05 + 0.1 * gray) * frac * nightK;
      uniforms.uVeil.value = Math.min(1, gray * 1.4);
      uniforms.uDark.value = Math.min(1, Math.max(0, (nightK - 0.5) / 0.4));
      if (sky) uniforms.uSky.value.copy(sky);
      const x = Math.min(1, Math.max(0, (e + 0.05) / 0.07));
      uniforms.uVis.value = x * x * (3 - 2 * x);
      mesh.visible = uniforms.uVis.value > 0;
      this.lit = frac * frac * Math.min(1, Math.max(0, e / 0.35)) * (1 - Math.min(1, gray * 1.4));
    },
  };
}
